import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import Header from "components/Headers/Header";
import {
  Card,
  Container,
  Row,
  Col,
  Table,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "reactstrap";
import {
  fetchBoostTransactions,
  fetchBoostMetrics,
  clearSelectedTransaction,
  loadMoreBoostTransactions,
} from "store/actions/adminBoostActions";

const VisibilityBoardPayments = () => {
  const dispatch = useDispatch();
  const { transactions, metrics, selectedTransaction, loading, loadingMore, hasMore, lastDoc, metricsLoading, detailLoading } =
    useSelector((state) => state.adminBoost);

  const [detailModalOpen, setDetailModalOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchBoostMetrics());
    dispatch(fetchBoostTransactions());
  }, [dispatch]);

  const handleViewDetails = (tx) => {
    dispatch({ type: "SET_BOOST_SELECTED_TRANSACTION", payload: tx });
    setDetailModalOpen(true);
  };

  const handleCloseModal = () => {
    setDetailModalOpen(false);
    dispatch(clearSelectedTransaction());
  };

  const getBoostTypeBadge = (type) => {
    const map = {
      boost: { color: "primary", label: "Boost" },
      shoutout: { color: "success", label: "Opinion + Boost" },
      selfBoost: { color: "warning", label: "Self Boost" },
      promoteManager: { color: "info", label: "Promote Manager" },
    };
    const b = map[type] || { color: "secondary", label: type || "N/A" };
    return <Badge color={b.color}>{b.label}</Badge>;
  };

  const isActive = (periodEnd) => {
    if (!periodEnd) return false;
    const d = periodEnd.toDate ? periodEnd.toDate() : new Date(periodEnd);
    return d > new Date();
  };

  const formatDate = (ts) => {
    if (!ts) return "N/A";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return moment(d).format("MMM DD, YYYY HH:mm");
  };

  const athleteName = (profile) =>
    profile?.name ||
    `${profile?.first_name || ""} ${profile?.last_name || ""}`.trim() ||
    "Unknown";

  const fanName = (profile) =>
    profile?.name ||
    `${profile?.first_name || ""} ${profile?.last_name || ""}`.trim() ||
    "N/A";

  return (
    <>
      <Header />
      <Container fluid className="mb-5">

        {/* Page heading */}
        <div className="d-flex justify-content-between align-items-center">
          <h2>Visibility Board Payments</h2>
        </div>

        {/* Metric cards — same style as dashboard */}
        <Row className="justify-content-center mt-3">
          <Col md={12} lg={4} align="center">
            <div className="dashboard-card">
              <p className="dashboard-card-numbers">
                {metricsLoading ? <Spinner /> : "$" + (metrics.totalRevenue || 0).toFixed(2)}
              </p>
              <p className="dashboard-card-title">Total Revenue</p>
            </div>
          </Col>
          <Col md={12} lg={4} align="center">
            <div className="dashboard-card">
              <p className="dashboard-card-numbers">
                {metricsLoading ? <Spinner /> : metrics.totalTransactions}
              </p>
              <p className="dashboard-card-title">Total Transactions</p>
            </div>
          </Col>
          <Col md={12} lg={4} align="center">
            <div className="dashboard-card">
              <p className="dashboard-card-numbers">
                {metricsLoading ? <Spinner /> : metrics.activeBoosts}
              </p>
              <p className="dashboard-card-title">Active Boosts</p>
            </div>
          </Col>
        </Row>

        {/* Transactions table — same card/table pattern as SportsCommunityBadge */}
        <Card className="p-2 mt-3">
          {loading ? (
            <div className="text-center my-4">
              <Spinner size="lg" color="primary" />
            </div>
          ) : (
            <Table className="align-items-center table-flush mt-3" responsive>
              <thead>
                <tr>
                  <th className="custom-table-heading custom-heading" style={{ width: "220px", maxWidth: "220px" }}>Payment ID</th>
                  <th className="custom-table-heading custom-heading">Date</th>
                  <th className="custom-table-heading custom-heading">Athlete / Manager</th>
                  <th className="custom-table-heading custom-heading">Boost Type</th>
                  <th className="custom-table-heading custom-heading">Duration</th>
                  <th className="custom-table-heading custom-heading">Price / Day</th>
                  <th className="custom-table-heading custom-heading">Total</th>
                  <th className="custom-table-heading custom-heading">Fan</th>
                  <th className="custom-table-heading custom-heading">Status</th>
                  <th className="custom-table-heading custom-heading">Details</th>
                </tr>
              </thead>
              <tbody className="custom-table-body mt-3">
                {transactions.length > 0 ? (
                  transactions.map((tx) => (
                    <tr key={tx.id}>
                      <td className="custom-table-text" style={{ width: "220px", maxWidth: "220px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <small>{tx.paymentId || tx.id}</small>
                      </td>
                      <td className="custom-table-text truncated-text">
                        {formatDate(tx.createdAt)}
                      </td>
                      <td className="custom-table-text">
                        <div className="d-flex align-items-center">
                          {tx.athleteProfile?.player_image?.url || tx.athleteProfile?.photo ? (
                            <img
                              src={tx.athleteProfile.player_image?.url || tx.athleteProfile.photo}
                              alt=""
                              className="rounded-circle mr-2"
                              style={{ width: 30, height: 30, objectFit: "cover", flexShrink: 0 }}
                            />
                          ) : (
                            <div
                              className="rounded-circle mr-2 bg-secondary d-flex align-items-center justify-content-center"
                              style={{ width: 30, height: 30, flexShrink: 0 }}
                            >
                              <i className="ni ni-single-02 text-white" style={{ fontSize: 12 }} />
                            </div>
                          )}
                          <div>
                            <div>{athleteName(tx.athleteProfile)}</div>
                            <small className="text-muted">{tx.athleteProfile?.email || ""}</small>
                          </div>
                        </div>
                      </td>
                      <td className="custom-table-text">
                        {getBoostTypeBadge(tx.featureType)}
                      </td>
                      <td className="custom-table-text">
                        {tx.duration} day{tx.duration !== 1 ? "s" : ""}
                      </td>
                      <td className="custom-table-text">
                        ${(tx.pricePerDay || 0).toFixed(2)}
                      </td>
                      <td className="custom-table-text">
                        <strong>${((tx.totalAmount || 0) - 0.50).toFixed(2)}</strong>
                      </td>
                      <td className="custom-table-text truncated-text">
                        {fanName(tx.fanProfile)}
                      </td>
                      <td className="custom-table-text">
                        {isActive(tx.periodEnd) ? (
                          <Badge color="success">Active</Badge>
                        ) : (
                          <Badge color="danger">Expired</Badge>
                        )}
                      </td>
                      <td className="card-subtitle">
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => handleViewDetails(tx)}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10} className="text-center custom-table-text py-4">
                      No transactions found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
          {!loading && hasMore && (
            <div className="text-center my-3">
              <Button
                color="primary"
                disabled={loadingMore}
                onClick={() => dispatch(loadMoreBoostTransactions(lastDoc))}
              >
                {loadingMore ? <Spinner size="sm" /> : "Load More"}
              </Button>
            </div>
          )}
        </Card>
      </Container>

      {/* Transaction Details Modal */}
      <Modal isOpen={detailModalOpen} toggle={handleCloseModal} size="lg" centered>
        <ModalHeader toggle={handleCloseModal}>Transaction Details</ModalHeader>
        <ModalBody>
          {detailLoading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : selectedTransaction ? (
            <>
              {/* Athlete Profile */}
              <Card className="p-3 mb-3">
                <h5 className="mb-3">Athlete / Manager Profile</h5>
                <Row>
                  <Col xs={3} className="text-center">
                    {selectedTransaction.athleteProfile?.player_image?.url ||
                    selectedTransaction.athleteProfile?.photo ? (
                      <img
                        src={
                          selectedTransaction.athleteProfile.player_image?.url ||
                          selectedTransaction.athleteProfile.photo
                        }
                        alt=""
                        className="rounded-circle"
                        style={{ width: 80, height: 80, objectFit: "cover" }}
                      />
                    ) : (
                      <div
                        className="rounded-circle bg-secondary d-flex align-items-center justify-content-center mx-auto"
                        style={{ width: 80, height: 80 }}
                      >
                        <i className="ni ni-single-02 text-white" style={{ fontSize: "1.5rem" }} />
                      </div>
                    )}
                  </Col>
                  <Col xs={9}>
                    <p className="mb-1"><strong>Name:</strong> {athleteName(selectedTransaction.athleteProfile)}</p>
                    <p className="mb-1"><strong>Email:</strong> {selectedTransaction.athleteProfile?.email || "N/A"}</p>
                    <p className="mb-1"><strong>Sport:</strong> {selectedTransaction.athleteProfile?.sport || "N/A"}</p>
                    <p className="mb-1">
                      <strong>ID:</strong>{" "}
                      {selectedTransaction.athleteProfile?.athleteID ||
                        selectedTransaction.athleteProfile?.managerID ||
                        selectedTransaction.athleteId}
                    </p>
                    {selectedTransaction.athleteProfile?.isManager && (
                      <Badge color="info" className="mr-1">Manager</Badge>
                    )}
                    {selectedTransaction.athleteProfile?.managerCertified && (
                      <Badge color="success">Certified</Badge>
                    )}
                  </Col>
                </Row>
              </Card>

              {/* Payment Details */}
              <Card className="p-3 mb-3">
                <h5 className="mb-3">Payment Details</h5>
                <Row>
                  <Col xs={6}>
                    <p className="mb-1"><strong>Payment ID:</strong></p>
                    <p className="mb-1 text-muted" style={{ wordBreak: "break-all", fontSize: 12 }}>
                      {selectedTransaction.paymentId || selectedTransaction.id}
                    </p>
                    <p className="mb-1"><strong>Method:</strong> {selectedTransaction.paymentMethod || "PayPal"}</p>
                    <p className="mb-1"><strong>Gross Amount:</strong> <span className="text-success font-weight-bold">${(selectedTransaction.totalAmount || 0).toFixed(2)}</span></p>
                    <p className="mb-1"><strong>PayPal Fee:</strong> $0.50</p>
                    <p className="mb-1"><strong>Net Amount:</strong> ${((selectedTransaction.totalAmount || 0) - 0.5).toFixed(2)}</p>
                  </Col>
                  <Col xs={6}>
                    <p className="mb-1"><strong>Date:</strong> {formatDate(selectedTransaction.createdAt)}</p>
                    <p className="mb-1"><strong>Fan:</strong> {fanName(selectedTransaction.fanProfile)}</p>
                    <p className="mb-1"><strong>Fan Email:</strong> {selectedTransaction.fanProfile?.email || "N/A"}</p>
                  </Col>
                </Row>
              </Card>

              {/* Boost Details */}
              <Card className="p-3 mb-3">
                <h5 className="mb-3">Boost Details</h5>
                <Row>
                  <Col xs={6}>
                    <p className="mb-1"><strong>Type:</strong> {getBoostTypeBadge(selectedTransaction.featureType)}</p>
                    <p className="mb-1"><strong>Duration:</strong> {selectedTransaction.duration} day{selectedTransaction.duration !== 1 ? "s" : ""}</p>
                    <p className="mb-1"><strong>Price/Day:</strong> ${(selectedTransaction.pricePerDay || 0).toFixed(2)}</p>
                  </Col>
                  <Col xs={6}>
                    <p className="mb-1"><strong>Start:</strong> {formatDate(selectedTransaction.periodStart)}</p>
                    <p className="mb-1"><strong>End:</strong> {formatDate(selectedTransaction.periodEnd)}</p>
                    <p className="mb-1">
                      <strong>Status:</strong>{" "}
                      {isActive(selectedTransaction.periodEnd) ? (
                        <>
                          <Badge color="success">Active</Badge>
                          <span className="ml-2 text-muted" style={{ fontSize: 12 }}>
                            {moment(
                              selectedTransaction.periodEnd?.toDate
                                ? selectedTransaction.periodEnd.toDate()
                                : selectedTransaction.periodEnd
                            ).fromNow(true)} remaining
                          </span>
                        </>
                      ) : (
                        <Badge color="danger">Expired</Badge>
                      )}
                    </p>
                  </Col>
                </Row>
                {selectedTransaction.opinion && (
                  <div className="mt-2">
                    <strong>Shoutout Opinion:</strong>
                    <p className="mt-1 p-2 bg-light rounded">{selectedTransaction.opinion}</p>
                  </div>
                )}
              </Card>

              {/* Athlete Boost Summary */}
              {selectedTransaction.athleteBoostData && (
                <Card className="p-3">
                  <h5 className="mb-3">Athlete Boost Summary</h5>
                  <Row>
                    <Col xs={6}>
                      <p className="mb-1"><strong>Total Boosts:</strong> {selectedTransaction.athleteBoostData.totalBoosts || 0}</p>
                      <p className="mb-1"><strong>Total Duration:</strong> {selectedTransaction.athleteBoostData.totalDuration || 0} days</p>
                    </Col>
                    <Col xs={6}>
                      <p className="mb-1"><strong>Total Spent:</strong> ${(selectedTransaction.athleteBoostData.totalAmountSpent || 0).toFixed(2)}</p>
                      <p className="mb-1"><strong>Last Boost:</strong> {formatDate(selectedTransaction.athleteBoostData.lastBoostTime)}</p>
                    </Col>
                  </Row>
                </Card>
              )}
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCloseModal}>Close</Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default VisibilityBoardPayments;
